import sx from '../sx.js';
import { themeGet } from '../utils/theme-get.js';
import React__default from 'react';
import styled from 'styled-components';
import Box from '../components/Box/Box.js';
import Text from '../components/Text/Text.js';
import AtlasButton from '../components/AtlasButton/AtlasButton.js';
import Spacer from '../components/Spacer/Spacer.js';
import MicroHeaderMenu from '../components/GettingStartedPage/MicroHeaderMenu.js';

/* eslint-disable @typescript-eslint/no-non-null-assertion */

/**
 * Every Getting Started page MicroHeader has several options for users
 * to take action, including a button with a primary action as well as
 * a dropdown or listbox that provides options for users to take.
 *
 * Those actions can be internal, external, or addon actions.
 *
 * Additionally, the MicroHeader can display a contextual menu that renders
 * a dropdown or listbox component, which you can pass into the MicroHeaderMenu
 * component as an argument.
 */

/**
 * The type of analytics metrics your application collects.
 */

const MicroHeaderWrapper = styled(Box).withConfig({
  displayName: "MicroHeader__MicroHeaderWrapper",
  componentId: "sc-103v5si-0"
})(["display:flex;gap:8px;align-items:center;flex-grow:1;", ""], sx);
const Logo = styled.img.withConfig({
  displayName: "MicroHeader__Logo",
  componentId: "sc-103v5si-1"
})(["width:20px;height:20px;display:block;"]);
const SVG = styled.svg.withConfig({
  displayName: "MicroHeader__SVG",
  componentId: "sc-103v5si-2"
})(["display:block;user-select:none;overflow:visible;"]);
const Title = styled(Text).withConfig({
  displayName: "MicroHeader__Title",
  componentId: "sc-103v5si-3"
})(["@media (min-width:350px){max-width:100px;margin:0;}@media (min-width:", "){max-width:revert;}"], themeGet('breakpoints.0'));

/**
 * A MicroHeader is a general purpose semantic header to announce the name of an application on a Getting Started
 * page and provide options for a user to take.
 *
 * It renders an application title, logo image, and accepts optional “action” props
 * that users can pass to a provided dropdown or listbox component, which it makes accessible to end-users via a contextual “More” menu icon.
 *
 * MicroHeader's `menuComponent` prop accepts a React component; that's meant to render a contextual menu, dropdown, or select menu of
 * of your choice, which itself may accept the `menuItems` and `actionHandler` props.
 *
 * For example:
 *
 * ```jsx
 * import Dropdown, {DropdownItem} from "@atlaskit/dropdown-menu";
 * import {MicroHeader, Footer, Stack} from "@fuegokit/react";
 *
 * const MyMenu = ({menuItems, actionHandler, action, ...props}) => {
 *   return (
 *      <Dropdown{...props}>
 *        {menuItems.map(item => {
 *          <DropdownItem key={item.id} onClick={() => myActionHandler(action)} />
 *         })}
 *      </Dropdown>
 *   )
 * }
 *
 * const MyApp = () => {
 *   return (
 *      <Stack size={7}> // margin-top: 56px for all elements but first
 *        <MicroHeader
 *          appLogoUrl={`https://www.mylogo.png`}
 *          title={`My product name`}
 *          menuComponent={
 *            <MyMenu
 *              menuItems={myMenuItems}
 *              actionHandler={myActionHandler}
 *              action={myAction} />
 *          }/>
 *         ...other elements
 *      </Stack>
 *      <Footer {...props}/>
 *   )
 * }
 * ```
 * See the docs on MicroHeader for more examples.
 */
const MicroHeader = ({
  title,
  appLogoUrl,
  actionHandler,
  feedbackAction,
  menuComponent,
  feedbackButtonIcon,
  feedbackButtonLabel,
  sx: sxProp
}) => {
  console.warn('Microheader is deprecated. Use the v1.1 layout for Getting Started screens instead. See: https://www.fuegokit.design/react-storybook/?path=/story/patterns-getting-started-page--getting-started-page-growth');
  return /*#__PURE__*/React__default.createElement(MicroHeaderWrapper, {
    as: "header",
    justifyContent: "space-between",
    flexDirection: "row"
  }, /*#__PURE__*/React__default.createElement(Logo, {
    src: appLogoUrl,
    alt: ""
  }), /*#__PURE__*/React__default.createElement(Box, {
    minWidth: "0px",
    display: "flex",
    marginRight: "auto",
    maxWidth: "100%",
    alignItems: "center",
    flex: "1 0 auto"
  }, /*#__PURE__*/React__default.createElement(Title, {
    as: "h2",
    role: "heading",
    fontSize: 1,
    fontWeight: "bold",
    color: "text.subtlest",
    margin: "auto",
    paddingLeft: "4px"
  }, ' ', title === null || title === void 0 ? void 0 : title.toUpperCase())), /*#__PURE__*/React__default.createElement(AtlasButton, {
    as: "button",
    onClick: () => actionHandler ? actionHandler(feedbackAction) : null,
    "data-testid": "microheader-btn"
  }, feedbackButtonIcon || /*#__PURE__*/React__default.createElement(SVG, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M10.881 5.47998L2.455 12.309C2.059 12.629 1.873 13.265 2.042 13.726L2.141 13.998C2.309 14.46 2.867 14.827 3.368 14.818L4.499 14.798L10.561 14.696L14.213 14.633C14.723 14.623 15.001 14.248 14.829 13.772L11.906 5.74198C11.801 5.45398 11.582 5.30098 11.339 5.30098C11.1702 5.30597 11.0084 5.36922 10.881 5.47998ZM4.98 15.953L6.734 20.771C6.77705 20.8968 6.84475 21.0127 6.93314 21.112C7.02153 21.2113 7.12884 21.292 7.24879 21.3494C7.36874 21.4067 7.49892 21.4396 7.63172 21.446C7.76451 21.4525 7.89726 21.4324 8.02219 21.3869C8.14713 21.3414 8.26174 21.2715 8.35932 21.1812C8.4569 21.0909 8.5355 20.982 8.59051 20.861C8.64552 20.7399 8.67585 20.6091 8.67971 20.4762C8.68357 20.3433 8.66089 20.211 8.613 20.087L7.074 15.859L4.98 15.953ZM18.691 6.84198L15.872 7.86798C15.7462 7.91103 15.6303 7.97873 15.5309 8.06713C15.4316 8.15552 15.3509 8.26283 15.2936 8.38278C15.2362 8.50272 15.2034 8.6329 15.197 8.7657C15.1905 8.8985 15.2106 9.03125 15.2561 9.15618C15.3016 9.28111 15.3715 9.39572 15.4618 9.49331C15.5521 9.59089 15.661 9.66948 15.782 9.7245C15.9031 9.77951 16.0338 9.80983 16.1667 9.81369C16.2996 9.81756 16.432 9.79488 16.556 9.74698L19.376 8.72098C19.4994 8.67601 19.6128 8.60716 19.7097 8.51836C19.8065 8.42957 19.8849 8.32257 19.9404 8.20348C19.9958 8.08439 20.0273 7.95554 20.033 7.82428C20.0387 7.69302 20.0185 7.56193 19.9735 7.43848C19.9285 7.31504 19.8597 7.20167 19.7709 7.10483C19.6821 7.008 19.5751 6.9296 19.456 6.87412C19.3369 6.81864 19.2081 6.78715 19.0768 6.78147C18.9455 6.77578 18.8144 6.79601 18.691 6.84098V6.84198ZM16.899 10.687C16.7341 10.7474 16.588 10.8498 16.475 10.9842C16.362 11.1186 16.2862 11.2802 16.255 11.453C16.2093 11.7142 16.269 11.9828 16.4211 12.2C16.5731 12.4173 16.805 12.5654 17.066 12.612L20.021 13.132C20.1916 13.162 20.367 13.1472 20.5301 13.089C20.6932 13.0309 20.8385 12.9313 20.9516 12.8002C21.0647 12.6691 21.1418 12.5108 21.1753 12.3409C21.2089 12.171 21.1977 11.9953 21.143 11.831L21.126 11.784C21.0638 11.624 20.9614 11.4827 20.8286 11.3739C20.6958 11.2651 20.5371 11.1925 20.368 11.163L17.413 10.642C17.2407 10.6105 17.0632 10.6261 16.899 10.687ZM16.351 3.04798L14.422 5.34598C14.2515 5.54914 14.1686 5.81172 14.1917 6.07596C14.2148 6.3402 14.3418 6.58445 14.545 6.75498C14.7482 6.92552 15.0107 7.00837 15.275 6.9853C15.5392 6.96223 15.7835 6.83514 15.954 6.63198L17.882 4.33398C18.0039 4.18811 18.0818 4.01052 18.1065 3.822C18.1312 3.63348 18.1017 3.44182 18.0215 3.26946C17.9412 3.09709 17.8135 2.95115 17.6533 2.84871C17.4932 2.74626 17.3071 2.69156 17.117 2.69098C16.9709 2.69048 16.8265 2.72222 16.6941 2.78393C16.5617 2.84564 16.4446 2.9358 16.351 3.04798Z",
    fill: "currentColor"
  })), /*#__PURE__*/React__default.createElement(Spacer, {
    axis: "horizontal",
    size: 4
  }), feedbackButtonLabel || 'Feedback'), menuComponent || /*#__PURE__*/React__default.createElement(MicroHeaderMenu, null));
};
MicroHeader.displayName = "MicroHeader";
MicroHeader.displayName = 'MicroHeader';

export { MicroHeader };
